import apiService, { ServiceType } from './api';

/**
 * Import jobs: a grid symbol import, a pair import and "End from broker" run in the background,
 * Postgres and the engine both change or neither (BE docs/IMPORT-AT-APPLY.md).
 */

export type ImportJobStatus =
    | 'PENDING'
    | 'PREPARING'
    | 'PREPARED'
    | 'COMMITTING_DB'
    | 'DB_COMMITTED'
    | 'COMMITTING_ENGINE'
    | 'READY'
    | 'FAILED'
    | 'CANCELLING'
    | 'CANCELLED';

export type ImportJobKind = 'GRID_SYMBOL' | 'GRID_SYMBOL_END' | 'PAIR_IMPORT' | 'PAIR_END';

export interface ImportJobView {
    id: string;
    kind: ImportJobKind;
    scope: 'GRID' | 'PAIR';
    status: ImportJobStatus;
    error: string | null;
    dataConfigId: string;
    portfolioId: string;
    attempts: number;
    /** The pair a PAIR_IMPORT created, once its DB commit has run. */
    resultDataConfigId: string | null;
    createdAt: string;
    updatedAt: string;
}

/** Socket.io `import-job` payload on the /bot namespace. */
export interface ImportJobEvent {
    id: string;
    status: ImportJobStatus;
    error: string | null;
}

export interface ImportStarted {
    importId: string;
}

// kept with the view helpers so they load without the HTTP client
export { FINAL_IMPORT_STATUSES, isFinalImportStatus } from '../utils/importJobView';

/** The 409 an import, grid Place or pair Start answers while a job on that namespace is open. */
export interface ImportInProgress {
    importId: string;
    status: ImportJobStatus | null;
    message: string;
}

export function importInProgressOf(err: unknown): ImportInProgress | null {
    const res = (err as { response?: { status?: number; data?: any } })?.response;
    const data = res?.data;
    if (res?.status !== 409 || data?.code !== 'IMPORT_IN_PROGRESS' || !data?.importId) return null;
    return {
        importId: String(data.importId),
        status: (data.status as ImportJobStatus) ?? null,
        message: String(data.message ?? 'An import on this account is not finished yet.'),
    };
}

const core = () => apiService.getService(ServiceType.CORE);

export async function getImportJob(id: string): Promise<ImportJobView> {
    const { data } = await core().get<ImportJobView>(`/imports/${id}`);
    return data;
}

/** Re-enqueue an unfinished job. 409 once it is final. */
export async function retryImportJob(id: string): Promise<ImportJobView> {
    const { data } = await core().post<ImportJobView>(`/imports/${id}/retry`);
    return data;
}

/** Cancel a job that is not READY; the server restores Postgres if it was already written. */
export async function cancelImportJob(id: string): Promise<ImportJobView> {
    const { data } = await core().post<ImportJobView>(`/imports/${id}/cancel`);
    return data;
}
